import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import ImageUpload from '@/components/ImageUpload';
import ImageCropper from '@/components/ImageCropper';
import Modal from '@/components/Modal';
import { uploadProfileImage, uploadSnapImage, deleteProfileImage, deleteSnapImage } from '@/lib/supabase-storage';

export type ImageType = 'profile' | 'snap';

interface ImageUploadManagerProps {
  currentImageUrl?: string | null;
  onImageUpdated: (newImageUrl: string) => void;
  imageType: ImageType;
  userId?: string;
  aspectRatio?: number;
  maxSizeMB?: number;
  shape?: 'square' | 'circle';
  className?: string;
  label?: string;
}

export default function ImageUploadManager({
  currentImageUrl,
  onImageUpdated,
  imageType,
  userId,
  aspectRatio = imageType === 'profile' ? 1 : 16/9,
  maxSizeMB = 1.5,
  shape = imageType === 'profile' ? 'circle' : 'square',
  className = '',
  label
}: ImageUploadManagerProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl);

  // Update preview when currentImageUrl changes
  useEffect(() => {
    if (currentImageUrl !== previewUrl) {
      setPreviewUrl(currentImageUrl);
    }
  }, [currentImageUrl, previewUrl, imageType]);

  const handleImageSelect = async (file: File) => {
    setSelectedFile(file);
    setTempImageUrl(URL.createObjectURL(file));
    setShowCropper(true);
    return null; // Return null as we're not uploading yet
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      setIsUploading(true);
      setShowCropper(false);
      
      // Create a preview of the cropped image
      const croppedPreviewUrl = URL.createObjectURL(croppedBlob);
      
      // Set the preview immediately to show the cropped image
      setPreviewUrl(croppedPreviewUrl);
      
      // Convert blob to file with proper name and type
      const fileName = selectedFile?.name || `${imageType}-image.jpg`;
      const file = new File([croppedBlob], fileName, { 
        type: 'image/jpeg' 
      });
      
      // Upload to Supabase Storage based on image type
      const { url, error } = imageType === 'profile' 
        ? await uploadProfileImage(file)
        : await uploadSnapImage(file);
      
      if (error || !url) {
        throw new Error(error?.message || 'Failed to upload image');
      }
      
      // Update UI with the permanent URL from storage
      onImageUpdated(url);
      toast.success(`${imageType === 'profile' ? 'Profile' : 'Image'} uploaded successfully`);
      
      // Clean up the temporary URL after the permanent one is set
      URL.revokeObjectURL(croppedPreviewUrl);
    } catch (error) {
      console.error(`Error uploading ${imageType} image:`, error);
      toast.error(`Failed to update ${imageType === 'profile' ? 'profile image' : 'image'}`);
      // Reset preview on error
      setPreviewUrl(currentImageUrl);
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      if (tempImageUrl) {
        URL.revokeObjectURL(tempImageUrl);
        setTempImageUrl(null);
      }
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedFile(null);
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
      setTempImageUrl(null);
    }
  };

  const handleRemoveImage = async () => {
    try {
      setIsUploading(true);
      
      // Only delete from storage if it's a Supabase URL
      if (currentImageUrl && currentImageUrl.includes('supabase.co')) {
        if (imageType === 'profile') {
          const { success, error } = await deleteProfileImage(currentImageUrl);
          if (!success) {
            console.error(`Error deleting profile image:`, error);
          }
        } else {
          const { success, error } = await deleteSnapImage(currentImageUrl);
          if (!success) {
            console.error(`Error deleting snap image:`, error);
          }
        }
      }
      
      // Update UI
      setPreviewUrl(null);
      onImageUpdated(''); // Pass empty string to clear the avatar_url in the database
      toast.success(`${imageType === 'profile' ? 'Profile image' : 'Image'} removed`);
    } catch (error) {
      console.error(`Error removing ${imageType} image:`, error);
      toast.error(`Failed to remove ${imageType === 'profile' ? 'profile image' : 'image'}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="h-full w-full">
        <ImageUpload
          onImageUpload={handleImageSelect}
          onImageRemove={handleRemoveImage}
          currentImageUrl={previewUrl}
          className={className}
          maxSizeMB={maxSizeMB}
          shape={shape}
          label={label}
          isUploading={isUploading}
        />
      </div>
      
      <Modal
        isOpen={showCropper}
        onClose={handleCropCancel}
        title={imageType === 'profile' ? "Crop Profile Picture" : "Crop Image"}
      >
        {tempImageUrl && (
          <ImageCropper
            imageUrl={tempImageUrl}
            aspect={aspectRatio}
            onCropComplete={handleCropComplete}
            onCancel={handleCropCancel}
          />
        )}
      </Modal>
    </>
  );
}